import { type GetProductPricesQuery } from '@codezo/graphql';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { formatDate } from '../../lib/utils';

export type PriceRow = GetProductPricesQuery['getProductPrices'][number];

/** One supplier's prices, or the default ones. Null `vetterEntityId` is the default list. */
export type PriceList = {
	key: string;
	vetterEntityId: string | null;
	name: string;
	rows: PriceRow[];
};

const COLUMNS = 'grid-cols-[64px_120px_84px_120px_112px_minmax(0,1fr)_60px]';

export const money = (amount: number, currency: string) =>
	new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency,
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);

/** A per-piece price: 2 decimals, up to 4 only when the figure needs them. */
export const unitMoney = (amount: number, currency: string) =>
	new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency,
		minimumFractionDigits: 2,
		maximumFractionDigits: 4,
	}).format(amount);

/** Up to two decimals, trailing zeros dropped: 10%, 16.67%. */
const percent = (value: number) => `${Number(value.toFixed(2))}%`;

const days = (min?: number | null, max?: number | null) =>
	min == null ? null : max == null || max === min ? `${min} days` : `${min}–${max} days`;

/**
 * A row the accepted-offer write-back created and nobody authored: cost zero,
 * switched off, carrying only what the tier was last quoted at. It is evidence
 * that a price is missing, not a price of zero.
 */
export const isNeverAuthored = (row: PriceRow) =>
	!row.isActive && row.baseUnitPrice === 0 && row.lastQuotedUnitPrice != null;

/**
 * Splits the flat list into one list per supplier, default first, then
 * suppliers by name, each by quantity. Interleaved by quantity, three ladders
 * read as one and none of them could be followed top to bottom.
 */
export function groupPriceLists(rows: PriceRow[]): PriceList[] {
	const byKey = new Map<string, PriceList>();
	for (const row of rows) {
		const key = row.vetterEntityId ?? 'default';
		const list = byKey.get(key) ?? {
			key,
			vetterEntityId: row.vetterEntityId ?? null,
			name: row.vetterEntity?.name ?? 'Default price',
			rows: [],
		};
		list.rows.push(row);
		byKey.set(key, list);
	}
	return [...byKey.values()]
		.map((l) => ({ ...l, rows: [...l.rows].sort((a, b) => a.quantity - b.quantity) }))
		.sort(
			(a, b) =>
				Number(a.vetterEntityId != null) - Number(b.vetterEntityId != null) ||
				a.name.localeCompare(b.name)
		);
}

export function PriceListBlock({
	list,
	onAdd,
	onEdit,
	onDelete,
}: {
	list: PriceList;
	onAdd: () => void;
	onEdit: (row: PriceRow) => void;
	onDelete: (row: PriceRow) => void;
}) {
	const isDefault = list.vetterEntityId == null;
	return (
		<div className="border-b border-line-1 pb-2 last:border-0">
			<div className="flex items-center justify-between gap-4 px-4 pb-2 pt-4">
				<div className="flex flex-col gap-0.5">
					<div className="flex items-baseline gap-2">
						<h4 className="text-sm font-medium text-text-1">{list.name}</h4>
						<span className="text-xs text-text-3">
							{list.rows.length} {list.rows.length === 1 ? 'price' : 'prices'}
						</span>
					</div>
					<span className="text-xs text-text-3">
						{isDefault
							? 'Used for any supplier without its own price at that quantity'
							: 'Supplier prices, used before the default price'}
					</span>
				</div>
				<AddPriceButton onClick={onAdd} />
			</div>

			<div className="overflow-x-auto">
				<div className="min-w-[640px]">
					<div
						className={`grid ${COLUMNS} gap-2 px-4 py-1.5 text-2xs font-medium uppercase tracking-[0.05em] text-text-4`}
					>
						<span>Qty</span>
						<span>Cost</span>
						<span>Markup</span>
						<span>Sale price</span>
						<span>Production</span>
						<span>Last quoted</span>
						<span />
					</div>
					{list.rows.map((row) => (
						<PriceListRow
							key={row.id}
							row={row}
							onEdit={() => onEdit(row)}
							onDelete={() => onDelete(row)}
						/>
					))}
				</div>
			</div>
		</div>
	);
}

/** The block's own "Add price", starting on this list's supplier. */
function AddPriceButton({ onClick }: { onClick: () => void }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="flex h-7 items-center gap-1.5 rounded-md border border-line-2 px-2 text-xs text-text-2 transition-colors hover:bg-surface-fill hover:text-text-1"
		>
			<Plus size={12} />
			Add price
		</button>
	);
}

function PriceListRow({
	row,
	onEdit,
	onDelete,
}: {
	row: PriceRow;
	onEdit: () => void;
	onDelete: () => void;
}) {
	const neverAuthored = isNeverAuthored(row);
	/* The header-currency figures. Null only when no rate exists, and then the
	   stored figures are shown as they are. */
	const fig = row.converted;
	const shown = fig?.currency ?? row.currency;
	const sale = fig ? fig.unitSalePrice : row.unitSalePrice;
	const production = days(row.minProductionDays, row.maxProductionDays);

	return (
		<div
			className={`grid ${COLUMNS} min-h-10 items-center gap-2 border-t border-line-1 px-4 text-sm`}
		>
			<span className="font-mono text-text-2">{row.quantity.toLocaleString()}</span>

			<span className="flex flex-col">
				{neverAuthored ? (
					<span
						className="text-text-3"
						title="Created from a quote. Nobody has set a cost for this quantity."
					>
						Not set
					</span>
				) : (
					<span
						className="font-mono font-medium text-text-1"
						title={
							fig && fig.currency !== row.currency
								? `Stored as ${unitMoney(row.baseUnitPrice, row.currency)}`
								: undefined
						}
					>
						{unitMoney(fig?.baseUnitPrice ?? row.baseUnitPrice, shown)}
					</span>
				)}
				{!row.isActive && !neverAuthored && (
					<span className="text-2xs text-text-4">Inactive</span>
				)}
			</span>

			{row.markupPercentage != null ? (
				<span className="text-text-2">{percent(row.markupPercentage)}</span>
			) : (
				<span className="text-text-3">No markup</span>
			)}

			{sale != null ? (
				<span className="font-mono font-medium text-text-1">{unitMoney(sale, shown)}</span>
			) : (
				<span className="text-text-3">Cost only</span>
			)}

			<span className="text-text-2">{production ?? '—'}</span>

			{/* When this quantity was last quoted. The date only: an amount
			    beside it read as a warning nobody wanted. */}
			<span className="whitespace-nowrap text-xs text-text-3">
				{row.lastQuotedAt ? formatDate(row.lastQuotedAt) : '—'}
			</span>

			<span className="flex items-center justify-end gap-0.5">
				<button
					type="button"
					onClick={onEdit}
					aria-label="Edit price"
					className="rounded p-1 text-text-4 transition-colors hover:text-text-1"
				>
					<Pencil size={14} />
				</button>
				<button
					type="button"
					onClick={onDelete}
					aria-label="Remove price"
					className="rounded p-1 text-text-4 transition-colors hover:text-destructive"
				>
					<Trash2 size={14} />
				</button>
			</span>
		</div>
	);
}
